import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useUserAccess } from '../hooks/useUserAccess';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';
import { fn } from '../api/functions';

export const Header = () => {
  const { currentUser, logout } = useAuth();
  const { userAccess, loading } = useUserAccess();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [logoError, setLogoError] = useState({ desktop: false, mobile: false });
  const [creatingDemo, setCreatingDemo] = useState(false);
  
  // Only show demo button if user hasn't made a purchase
  // Don't show while userAccess is loading to avoid flicker
  const showDemoButton = currentUser && !loading && (!userAccess || !userAccess.storyTokens || userAccess.storyTokens === 0);
  
  // DEBUG: Log button visibility logic
  console.log('🔍 Demo Button Debug:', {
    currentUser: !!currentUser,
    loading,
    userAccess,
    storyTokens: userAccess?.storyTokens,
    showDemoButton
  });

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
      setMobileMenuOpen(false); // Close mobile menu on logout
    } catch (error) {
      console.error('Failed to log out:', error);
    }
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const handleLogoError = (type) => {
    setLogoError(prev => ({ ...prev, [type]: true }));
  };

  const handleTryDemo = async () => {
    try {
      setCreatingDemo(true);
      const result = await fn.createDemoStory();
      const storyId = result.data.storyId;
      navigate(`/story/${storyId}`);
      setMobileMenuOpen(false);
    } catch (error) {
      console.error('Error creating demo story:', error);
      alert('Failed to create demo story. Please try again.');
    } finally {
      setCreatingDemo(false);
    }
  };

  return (
    <header className="memory-stream-gradient md:shadow-lg">
      <div className="md:container md:mx-auto md:px-4 px-0 md:py-4 py-0">
        <div className="flex items-center justify-between md:h-auto" style={{ minHeight: '125px', border: 'none' }}>
          {/* Logo - Responsive */}
          <Link to="/" className="flex items-center justify-start md:py-0 py-0 md:pl-0 pl-0" onClick={closeMobileMenu} style={{ border: 'none', outline: 'none' }}>
            {/* Desktop Logo - Hidden on mobile */}
            {!logoError.desktop ? (
              <img 
                src="/assets/logos/memory-stream-logo-desktop.png" 
                alt="Memory Stream" 
                className="hidden md:block h-8 w-auto"
                style={{ border: 'none', outline: 'none' }}
                onError={() => handleLogoError('desktop')}
              />
            ) : (
              <span className="hidden md:block text-2xl font-semibold text-white ms-heading">
                Memory Stream
              </span>
            )}
            
            {/* Mobile Logo - Square with no bottom border */}
            {!logoError.mobile ? (
              <img 
                src="/assets/logos/memory-stream-logo-square.png" 
                alt="Memory Stream" 
                className="block md:hidden"
                style={{ 
                  width: '125px', 
                  height: '125px', 
                  objectFit: 'contain',
                  objectPosition: 'center',
                  borderRadius: '0',
                  border: 'none',
                  marginLeft: '0',
                  outline: 'none',
                  backgroundColor: 'transparent'
                }}
                onError={() => handleLogoError('mobile')}
              />
            ) : (
              <span className="block md:hidden text-xl font-semibold text-white ms-heading">
                MS
              </span>
            )}
            
            {/* Screen reader text */}
            <span className="sr-only">Memory Stream</span>
          </Link>
          
          {currentUser && (
            <>
              {/* Desktop Navigation */}
              <nav className="hidden md:flex items-center space-x-6">
                <Link
                  to="/story/new"
                  className="bg-white hover:bg-gray-50 font-medium px-4 py-2 rounded-md transition-colors"
                  style={{ color: 'var(--ms-primary)' }}
                >
                  New Story
                </Link>
                {showDemoButton && (
                  <button
                    onClick={handleTryDemo}
                    disabled={creatingDemo}
                    className="font-medium px-4 py-2 rounded-md transition-colors disabled:opacity-50"
                    style={{ 
                      backgroundColor: 'var(--ms-secondary)',
                      color: 'white'
                    }}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#A85F00'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'var(--ms-secondary)'}
                  >
                    {creatingDemo ? 'Creating...' : 'Try Demo'}
                  </button>
                )}
                <Link
                  to="/"
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  Dashboard
                </Link>
                <Link
                  to="/profile"
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  Profile
                </Link>
                <button
                  onClick={handleLogout}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  Logout
                </button>
              </nav>

              {/* Mobile Hamburger Button */}
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="md:hidden text-white hover:text-gray-200 transition-colors p-2"
                aria-label="Toggle navigation menu"
              >
                {mobileMenuOpen ? (
                  <XMarkIcon className="h-6 w-6" />
                ) : (
                  <Bars3Icon className="h-6 w-6" />
                )}
              </button>
            </>
          )}
        </div>

        {/* Mobile Navigation Menu */}
        {currentUser && mobileMenuOpen && (
          <nav className="md:hidden mt-4 pb-4" style={{ borderTop: '1px solid rgba(255,255,255,0.2)' }}>
            <div className="flex flex-col space-y-3 pt-4">
              <Link
                to="/story/new"
                onClick={closeMobileMenu}
                className="bg-white hover:bg-gray-50 font-medium px-4 py-3 rounded-md transition-colors text-center"
                style={{ color: 'var(--ms-primary)' }}
              >
                New Story
              </Link>
              {showDemoButton && (
                <button
                  onClick={handleTryDemo}
                  disabled={creatingDemo}
                  className="font-medium px-4 py-3 rounded-md transition-colors disabled:opacity-50 text-white"
                  style={{ backgroundColor: 'var(--ms-secondary)' }}
                >
                  {creatingDemo ? 'Creating...' : 'Try Demo'}
                </button>
              )}
              <Link
                to="/"
                onClick={closeMobileMenu}
                className="text-white hover:text-gray-200 transition-colors py-2 px-2"
              >
                Dashboard
              </Link>
              <Link
                to="/profile"
                onClick={closeMobileMenu}
                className="text-white hover:text-gray-200 transition-colors py-2 px-2"
              >
                Profile
              </Link>
              <button
                onClick={handleLogout}
                className="text-white hover:text-gray-200 transition-colors py-2 px-2 text-left"
              >
                Logout
              </button>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};
